import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import type { SetupStatusResponse } from '../services/apiService';
import { buildBookmarkletHref } from '../utils/bookmarklets';

type StepState = 'complete' | 'active' | 'pending';

const errorBannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '1rem',
  padding: '1rem',
  backgroundColor: 'var(--danger-bg)',
  color: 'var(--danger)',
  borderRadius: 'var(--radius)',
  border: '1px solid rgba(180,35,24,0.24)',
};

const retryButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: 'var(--danger)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const stepStyle = (state: StepState): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  padding: '0.75rem 1rem',
  borderRadius: 'var(--radius)',
  backgroundColor: state === 'active' ? 'var(--brand-soft)' : 'transparent',
  opacity: state === 'pending' ? 0.55 : 1,
});

const badgeStyle = (state: StepState): React.CSSProperties => ({
  flexShrink: 0,
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  fontWeight: 700,
  color: 'white',
  backgroundColor: state === 'complete' ? 'var(--good)' : state === 'active' ? 'var(--brand)' : 'var(--muted)',
});

interface Step {
  id: 'verify' | 'feed' | 'widget' | 'match' | 'measure';
  title: string;
  state: StepState;
  /**
   * An optional step can never be "where you are". Completion is the first
   * false REQUIRED step, so a publisher who declines measurement still sees a
   * finished checklist — a permanently unticked step reads as a broken product.
   */
  optional?: boolean;
}

// The setup status arrives as props: this component and FeedHealthBanner read the
// same endpoint, and the page now fetches it once for both (six queries per
// dashboard load became three). `onRetry` comes down with it because the error
// state below belongs to this component — the page owns the request, each child
// still owns what it shows.
export interface ActivationChecklistProps {
  status: SetupStatusResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

const ActivationChecklist: React.FC<ActivationChecklistProps> = ({ status, loading, error, onRetry }) => {
  // Website verification gates everything: a pending account cannot serve
  // matches, so it is the checklist's FIRST step. Status comes from the
  // existing AuthContext profile fetch — no extra request here, and deliberately
  // NOT from setup-status: that endpoint served a `siteVerified` boolean for a
  // while and nothing ever read it, because a boolean cannot express the third
  // state this component depends on (see `verifyBlocks` below) and because
  // AuthContext is the read WebsiteVerification can refresh after a successful
  // verify. Removed from the API 2026-09-06; the reasoning is in user-setup.ts.
  const { verificationStatus } = useAuth();
  // Only an EXPLICIT pending/failed status makes verification the active,
  // blocking first step. A null/undefined status (AuthContext hasn't loaded
  // the profile yet, or its fetch silently failed) is UNKNOWN — treating it
  // as "unverified" would nag a fully-set-up, verified account forever and
  // never let it reach "complete". Unknown is therefore non-blocking.
  const verifyBlocks = verificationStatus != null && verificationStatus !== 'verified';
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [apiKeyError, setApiKeyError] = useState(false);

  // REQUIRED steps only. The measurement step is deliberately excluded: it is
  // an upgrade, not a gate, so it must not be able to hold the checklist open.
  const allComplete = !verifyBlocks && !!status && status.feedUploaded && status.widgetSeen && status.firstMatch;
  // Measurement is settled either by a working credential or by an ingested
  // export — the file path needs no credential at all, and for 2Performant it
  // is the only path that does not require the publisher's account password.
  const measurementSettled = !!status && (status.conversionMeasured || status.conversionSourceConnected);

  // Only needed for step ②'s "try it on your site" CTA — fetched once, lazily,
  // and independently of EmbedCodeGenerator's own key fetch (see
  // utils/bookmarklets.ts for the shared builder that avoids duplicating the
  // construction logic).
  useEffect(() => {
    if (!status || allComplete || apiKey || apiKeyError) return;
    apiService.getApiKey()
      .then(setApiKey)
      .catch(() => setApiKeyError(true));
  }, [status, allComplete, apiKey, apiKeyError]);

  if (loading) {
    return (
      <section className="stats-section" data-testid="activation-checklist-loading">
        <h2>Activate shoppable images</h2>
        <p className="no-data">Loading your activation progress…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="stats-section" data-testid="activation-checklist">
        <h2>Activate shoppable images</h2>
        <div data-testid="activation-checklist-error" style={errorBannerStyle}>
          <span>{error}</span>
          <button data-testid="activation-checklist-retry" onClick={onRetry} style={retryButtonStyle}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  if (!status) return null;

  // TWO PRESENTATIONS of the same booleans, switched on whether every REQUIRED
  // step is already true.
  //
  // Required steps done + measurement settled -> nothing at all
  // (portal-ux-clarity: a finished checklist renders nothing, no lingering
  // "Setup complete" panel).
  //
  // Required steps done + measurement open -> ONE card, never a checklist. For
  // a publisher who has been live for months this is the only place they will
  // ever learn measurement exists; showing them a numbered "Get started" list
  // would read as the product having forgotten who they are.
  if (allComplete) {
    if (measurementSettled) return null;
    return (
      <section className="stats-section" data-testid="measurement-offer">
        <h2>Measure what your overlays actually earn</h2>
        <p style={{ color: 'var(--muted)', marginBottom: '0.75rem' }}>
          Your overlays are working. Right now their earnings are an{' '}
          <strong>estimate</strong> based on the EPC you set — connect your affiliate
          network, or upload its commissions export, and we can show you what they
          really earned instead.
        </p>
        <Link to="/networks" data-testid="measurement-offer-cta">Set up earnings tracking →</Link>
      </section>
    );
  }

  // Ordered funnel: the first false step is "active" (gets the CTA), every
  // step after it is "pending", every step before it is "complete". There is
  // no world where a later step is true while an earlier one is false (the
  // funnel is sequential in practice), but state is still derived
  // independently per-step from setup-status rather than assumed.
  const steps: Step[] = [
    // Verification first: until the website is verified, no matches are ever
    // served, so the later steps stay pending regardless of their own flags.
    { id: 'verify', title: 'Confirm you control the publisher site', state: verifyBlocks ? 'active' : 'complete' },
    {
      id: 'feed',
      title: 'Choose an affiliate product feed',
      state: verifyBlocks ? 'pending' : status.feedUploaded ? 'complete' : 'active',
    },
    {
      id: 'widget',
      title: 'Install ImgAff on the site',
      state: verifyBlocks || !status.feedUploaded ? 'pending' : status.widgetSeen ? 'complete' : 'active',
    },
    {
      id: 'match',
      // "Served", not "seen": this ticks on a match the SERVER sent, which is
      // not evidence anything was rendered. The milestone code keeps
      // first_overlay_viewed separate for exactly that reason.
      title: 'Observe the first match served',
      state:
        verifyBlocks || !status.feedUploaded || !status.widgetSeen
          ? 'pending'
          : status.firstMatch
            ? 'complete'
            : 'active',
    },
    {
      id: 'measure',
      title: 'Measure real earnings (optional)',
      optional: true,
      // Never 'active': an optional step is an offer, not the place a publisher
      // has got stuck. It ticks when measurement is settled and otherwise waits
      // quietly, so it cannot make a working setup look unfinished.
      state: measurementSettled ? 'complete' : 'pending',
    },
  ];

  const bookmarkletHref = apiKey ? buildBookmarkletHref(apiKey) : '';

  const guideTip = (
    <p style={{ marginTop: '0.8rem', marginBottom: 0, color: 'var(--muted)', fontSize: '0.88rem' }}>
      Tip: accurate image descriptions and captions give matching better evidence — the{' '}
      <a href="https://imgaff.com/targeting-guide.html" target="_blank" rel="noreferrer">
        Image Targeting Guide
      </a>{' '}
      shows how to describe images so the right products appear.
    </p>
  );

  return (
    <section className="stats-section" data-testid="activation-checklist">
      <h2>Activate shoppable images</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {steps.map((step, index) => (
          <div key={step.id} data-testid={`activation-step-${step.id}`} data-state={step.state} style={stepStyle(step.state)}>
            <span style={badgeStyle(step.state)} aria-hidden="true">
              {step.state === 'complete' ? '✓' : index + 1}
            </span>
            <div>
              <div style={{ fontWeight: step.state === 'active' ? 700 : 500 }}>{step.title}</div>

              {step.id === 'verify' && step.state === 'active' && (
                // The Website Verification workflow already lives further down
                // this same dashboard page — a native anchor scrolls to it.
                <a href="#website-verification" data-testid="activation-cta-verify" className="step-cta">
                  Verify your website</a>
              )}

              {step.id === 'feed' && step.state === 'active' && (
                <Link to="/feeds/upload" data-testid="activation-cta-feed" className="step-cta">
                  Upload your feed
                </Link>
              )}

              {step.id === 'widget' && step.state === 'active' && (
                <>
                  <Link to="/embed" data-testid="activation-cta-embed" className="step-cta">
                    Get the embed code
                  </Link>
                  {apiKey && (
                    <a
                      href={bookmarkletHref}
                      data-testid="activation-cta-bookmarklet"
                      onClick={(e) => {
                        e.preventDefault();
                        window.alert('Drag this link to your bookmarks bar, then click it while viewing your own website.');
                      }}
                      className="bookmarklet-btn"
                    >
                      Try it on your site
                    </a>
                  )}
                </>
              )}

              {/* A SIGNPOST, so it carries the place's name as the nav shows it.
                  Its sibling above ("Try it on your site") is action-shaped and
                  stays that way — it fires a bookmarklet rather than going
                  somewhere. This one goes to a screen the publisher will need to
                  find again later, through a nav that says "Fix matches". */}
              {step.id === 'match' && step.state === 'active' && (
                <Link to="/debug" data-testid="activation-cta-test-matching" className="step-cta">
                  Fix matches
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
      {guideTip}
    </section>
  );
};

export default ActivationChecklist;
